import React from 'react';
import { Upload, X, FileVideo, FileAudio, FileImage } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const getFileIcon = (file) => {
  if (file.type.startsWith('video/')) return <FileVideo className="w-8 h-8 text-blue-400" />;
  if (file.type.startsWith('audio/')) return <FileAudio className="w-8 h-8 text-purple-400" />;
  if (file.type.startsWith('image/')) return <FileImage className="w-8 h-8 text-green-400" />;
  return <Upload className="w-8 h-8 text-gray-400" />;
};

export const FileSelector = ({ 
  files, 
  uploadMode, 
  uploading, 
  uploadProgress, 
  onFileSelect, 
  onRemoveFile 
}) => {
  return (
    <>
      {/* File Selection Zone */}
      <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-gray-500 transition-colors">
        <input
          type="file"
          id="file-upload"
          multiple={uploadMode === 'batch'}
          accept="video/*,audio/*,image/*"
          onChange={onFileSelect}
          className="hidden"
          disabled={uploading}
          data-testid="file-input"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-white font-medium mb-2">
            Click to select {uploadMode === 'batch' ? 'files' : 'a file'}
          </p>
          <p className="text-gray-400 text-sm">
            Supported: Video, Audio, Images (Max 500MB per file)
          </p>
        </label>
      </div>

      {/* Selected Files List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-white font-semibold">Selected Files ({files.length})</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {files.map((file, index) => (
              <div 
                key={`file-${index}-${file.name}`} 
                className="bg-gray-800 rounded-lg p-3 flex items-center justify-between"
                data-testid={`selected-file-${index}`}
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(file)}
                  <div>
                    <p className="text-white text-sm font-medium">{file.name}</p>
                    <p className="text-gray-400 text-xs">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                
                {uploading && uploadProgress[file.name] !== undefined ? (
                  <div className="flex items-center gap-2">
                    <Progress value={uploadProgress[file.name]} className="w-24" />
                    <span className="text-sm text-gray-400">{uploadProgress[file.name]}%</span>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemoveFile(index)}
                    disabled={uploading}
                    data-testid={`remove-file-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default FileSelector;
